import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useQuery } from "@apollo/client/react";

import AssetDetailsModal from "../components/assets/AssetDetailsModal";
import { ASSET_QUERY } from "../../../constants/graphql/queries";
import { useAssetsCollectionSafe } from "../../../contexts/assetsCollection";

const CaptureEditAssetContext = React.createContext(null);

export const EditAssetProvider = ({ children, id }) => {
  const { clientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const assetsCollection = useAssetsCollectionSafe();
  const collection = assetsCollection?.collection || [];
  const [asset, setAsset] = useState(null);
  const searchParams = new URLSearchParams(location.search);
  const assetId = searchParams.get("assetId");
  const receivingWizardOpen = searchParams.get("receiving") === "true";
  const closingAssetIdRef = useRef(null);

  const updateAssetIdParam = useCallback(
    nextAssetId => {
      const params = new URLSearchParams(location.search);

      if (nextAssetId) {
        params.set("assetId", nextAssetId);
      } else {
        params.delete("assetId");
      }

      if (params.toString() === location.search.replace(/^\?/, "")) return;

      navigate(
        {
          pathname: location.pathname,
          search: params.toString(),
          hash: location.hash,
        },
        { replace: true }
      );
    },
    [location.hash, location.pathname, location.search, navigate]
  );

  const openAsset = useCallback(
    nextAsset => {
      closingAssetIdRef.current = null;
      setAsset(nextAsset);
      updateAssetIdParam(nextAsset?.id);
    },
    [updateAssetIdParam]
  );

  const closeAsset = useCallback(() => {
    closingAssetIdRef.current = asset?.id ? String(asset.id) : assetId;
    setAsset(null);
    updateAssetIdParam(null);
  }, [asset, assetId, updateAssetIdParam]);

  const matchingAsset = assetId ? collection.find(item => String(item.id) === assetId) : null;
  const needsLookup =
    !!assetId &&
    !receivingWizardOpen &&
    !matchingAsset &&
    closingAssetIdRef.current !== assetId &&
    String(asset?.id) !== assetId;

  const { data: fetchedData, loading: fetchingAsset } = useQuery(ASSET_QUERY, {
    variables: { id: assetId },
    skip: !needsLookup,
    fetchPolicy: "cache-first",
  });
  const fetchedAsset = fetchedData?.asset;

  useEffect(() => {
    if (receivingWizardOpen || !assetId) {
      closingAssetIdRef.current = null;
      if (asset) {
        setAsset(null);
      }
      return;
    }

    if (closingAssetIdRef.current === assetId) return;

    if (String(asset?.id) === assetId) return;

    if (matchingAsset) {
      setAsset(matchingAsset);
      return;
    }

    if (fetchedAsset && String(fetchedAsset.id) === assetId) {
      setAsset(fetchedAsset);
    }
  }, [asset, assetId, matchingAsset, fetchedAsset, receivingWizardOpen]);

  useEffect(() => {
    if (!needsLookup || fetchingAsset) return;
    if (fetchedData && !fetchedData.asset) {
      updateAssetIdParam(null);
    }
  }, [needsLookup, fetchingAsset, fetchedData, updateAssetIdParam]);

  return (
    <CaptureEditAssetContext.Provider value={{ setAsset: openAsset }}>
      {children}
      {asset && !receivingWizardOpen && (
        <AssetDetailsModal clientId={clientId} entity={asset} onClose={closeAsset} />
      )}
    </CaptureEditAssetContext.Provider>
  );
};

export const useEditAsset = () => React.useContext(CaptureEditAssetContext);
